import '@fontsource/inter/variable.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import App from './App';
import Gallery from './gallery/Gallery';
import './index.css';
import { worker } from './mocks/browser';
import Providers from './providers/Providers';
import { IdentityProvider } from './providers/IdentityProvider';
import LoginView from './views/LoginView';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DependencyGate } from './DependencyGate';
import { useSentry } from './sentry';
import AppErrorBoundary from './components/AppErrorBoundary';
import html2canvas from 'html2canvas';

// Used by ViuManager to get a screenshot of the UI
const handleScreenshotRequest = async (event: MessageEvent): Promise<void> => {
  if (event.data?.type !== 'screenshot-request') return;
  try {
    const canvas = await html2canvas(document.documentElement, {
      useCORS: true,
      allowTaint: true,
      width: window.innerWidth,
      height: window.innerHeight,
    });
    event.source!.postMessage(
      { type: 'screenshot-response', dataUrl: canvas.toDataURL('image/png') },
      { targetOrigin: event.origin } as WindowPostMessageOptions,
    );
  } catch (err) {
    event.source!.postMessage(
      { type: 'screenshot-error', error: String(err) },
      { targetOrigin: event.origin } as WindowPostMessageOptions,
    );
  }
};
window.addEventListener('message', (event) => {
  void handleScreenshotRequest(event);
});

async function prepare(): Promise<ServiceWorkerRegistration | undefined> {
  if (
    import.meta.env.MODE === 'galleryNoMsw' ||
    import.meta.env.MODE === 'gallery'
  ) {
    document.title = 'TenantViu Gallery';
  }
  if (import.meta.env.MODE === 'msw' || import.meta.env.MODE === 'gallery') {
    document.title += ' (MSW)';
    localStorage.setItem('id', 'J183002467');
    localStorage.setItem('type', 'eloview');
    localStorage.setItem(
      'psk',
      '5b915aca218a5e06c5782e93f87fff01c2d7f6eea5e36f224d37645a6d126b1e',
    );
    return worker.start({
      onUnhandledRequest(req) {
        if (new URL(req.url).origin === window.location.origin) {
          // Requests to the vite dev server itself (env.json, assets), so always ignore.
          return;
        }
        console.warn('Unhandled: %s %s', req.method, req.url);
      },
    });
  }

  return Promise.resolve(undefined);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchInterval: 60 * 60 * 1000, // Refetch every hour by default
    },
  },
});

const WithSentry: React.FC<{ children: React.ReactNode }> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useSentry();
  return <>{children}</>;
};

prepare()
  .then(() => {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <AppErrorBoundary>
          <WithSentry>
            <IntlProvider locale="sv-SE" defaultLocale="sv-SE">
              <QueryClientProvider client={queryClient}>
                <div className="h-screen bg-white">
                  <DependencyGate>
                    <IdentityProvider>
                      <MemoryRouter>
                        <Routes>
                          <Route path="/login" element={<LoginView />} />
                          <Route
                            path="*"
                            element={
                              <Providers>
                                {import.meta.env.MODE === 'gallery' ||
                                import.meta.env.MODE === 'galleryNoMsw' ? (
                                  <Gallery />
                                ) : (
                                  <App />
                                )}
                              </Providers>
                            }
                          />
                        </Routes>
                      </MemoryRouter>
                    </IdentityProvider>
                  </DependencyGate>
                </div>
              </QueryClientProvider>
            </IntlProvider>
          </WithSentry>
        </AppErrorBoundary>
      </React.StrictMode>,
    );
  })
  .catch((err: any) => {
    throw err;
  });
